import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import {
    getProductListDataViewProperty,
    isLoadMore,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    hidePagination: isLoadMore(state),
    pagination: getProductListDataViewProperty(state, "pagination"),
});

const mapDispatchToProps = {
    addProductFilters,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ProductListPaginationStyles {
    pagination?: PaginationPresentationProps;
}

export const paginationStyles: ProductListPaginationStyles = {
    pagination: {
        cssOverrides: {
            pagination: css`
                margin: 0 !important;
                align-items: center;
                justify-content: space-around;
                background: #f1f6fb;
                display: flex;
                flex-wrap: wrap;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            justify-content: end;
                        `,
                        css`
                            justify-content: flex-start;
                        `,
                        null,
                        null,
                        null,
                    ])}
                nav[role="navigation"] {
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                justify-content: end;
                                width: 100% !important;
                            `,
                            null,
                            null,
                            css`
                                /*margin-left: 1rem;*/
                                margin-top: 0.3rem;
                                align-items: end;
                            `,
                            null,
                        ])}
                    ul {
                        li {
                            button {
                                min-width: 24px;
                                span {
                                    font-size: 13px;
                                }
                            }
                            button[aria-current^="page"] {
                                border: 1px solid #3c75b5;
                                border-radius: 50%;
                                min-height: 24px;
                                line-height: normal;
                                padding: 0 7px;
                            }
                            button:disabled {
                                width: 15px;
                                span {
                                    color: #012f6d;
                                    opacity: 0.4;
                                }
                            }
                            button[data-test-selector^="paginationButtonFirst"],
                            button[data-test-selector^="paginationButtonPrevious"],
                            button[data-test-selector^="paginationButtonNext"],
                            button[data-test-selector^="paginationButtonLast"] {
                                width: 15px;
                                span {
                                    color: #012f6d;
                                }
                            }
                        }
                    }
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                width: 55%;
                            `,
                            css`
                                width: 50%;
                                justify-content: center;
                            `,
                            null,
                            null,
                            null,
                        ])}
                }
            `,

            perPageSelect: css`
                flex-basis: 35%;
                max-width: 35%;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            flex-basis: 100%;
                            max-width: 100%;
                            justify-content: end;
                        `,
                        css`
                            flex-basis: 50%;
                            max-width: 50%;
                            justify-content: flex-start;
                        `,
                        null,
                        null,
                        null,
                    ])}
                > div {
                    width: 220px;
                    > label {
                        width: auto;
                        text-transform: uppercase;
                        font-size: 14px;
                        padding-top: 11px;
                        padding: 11px 6px 0 5px;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    null,
                                    css`
                                        padding: 11px 6px 0 0;
                                    `,
                                ],
                                "max",
                            )}
                        + div {
                            width: calc(100% - 155px);
                            margin-top: 8px !important;
                            ${({ theme }: { theme: BaseTheme }) =>
                                breakpointMediaQueries(theme, [
                                    css`
                                        width: auto;
                                    `,
                                    null,
                                    null,
                                    null,
                                    null,
                                ])}
                            select {
                                font-size: 14px !important;
                                height: 26px;
                                padding: 0 10px 0 5px !important;
                                ${({ theme }: { theme: BaseTheme }) =>
                                    breakpointMediaQueries(theme, [
                                        css`
                                            margin-left: 5px;
                                            width: 50px !important;
                                        `,
                                        null,
                                        null,
                                        null,
                                        null,
                                    ])}
                                + span {
                                    width: 20px !important;
                                    height: 20px !important;
                                    padding: 0 !important;
                                }
                            }
                        }
                    }
                }
            `,
        },
    },
};

const styles = paginationStyles;

const ProductListPagination: FC<Props> = ({ addProductFilters, hidePagination, pagination }) => {
    const [shouldScrollToTop, setShouldScrollToTop] = useState(false);
    useEffect(() => {
        if (shouldScrollToTop) {
            window.scrollTo(0, 0);
            setShouldScrollToTop(false);
        }
    }, [pagination?.page]);

    const changePage = (newPageIndex: number) => {
        addProductFilters({ page: newPageIndex });
        setShouldScrollToTop(true);
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        addProductFilters({ pageSize: newPageSize });
    };

    if (hidePagination || !pagination || !pagination.totalItemCount) {
        return null;
    }

    return (
        <Pagination
            {...styles.pagination}
            resultsCount={pagination!.totalItemCount}
            currentPage={pagination!.page}
            resultsPerPage={pagination!.pageSize}
            resultsPerPageOptions={pagination!.pageSizeOptions}
            resultsPerPageLabel="Per Page"
            onChangePage={changePage}
            onChangeResultsPerPage={changeResultsPerPage}
            pageSizeCookie="ProductList-PageSize"
            data-test-selector="productList_pagination"
            prevPage={pagination.prevPageUri}
            nextPage={pagination.nextPageUri}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListPagination),
    definition: {
        group: "Product List",
        displayName: "Pagination",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
